import { doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { db, isFirebaseConfigured } from '../../firebase/client';

const SITE_SETTINGS_COLLECTION = 'siteSettings';
const MAINTENANCE_NOTICE_DOC = 'maintenanceNotice';

const EMPTY_MAINTENANCE_NOTICE = Object.freeze({
  message: '',
  isPublished: false,
  updatedAt: null,
  updatedBy: '',
});

/**
 * Returns a normalized maintenance notice payload.
 * @param {any} payload Raw payload.
 * @returns {{message: string, isPublished: boolean, updatedAt: any, updatedBy: string}}
 */
function normalizeMaintenanceNotice(payload) {
  return {
    message: String(payload?.message || '').trim(),
    isPublished: Boolean(payload?.isPublished),
    updatedAt: payload?.updatedAt || null,
    updatedBy: String(payload?.updatedBy || ''),
  };
}

/**
 * Returns an empty maintenance notice value.
 * @returns {{message: string, isPublished: boolean, updatedAt: any, updatedBy: string}}
 */
export function getEmptyMaintenanceNotice() {
  return {
    ...EMPTY_MAINTENANCE_NOTICE,
  };
}

/**
 * Subscribes to the site-wide maintenance warning notice.
 * @param {(notice: {message: string, isPublished: boolean, updatedAt: any, updatedBy: string}) => void} onChange Callback.
 * @param {(error: Error) => void} [onError] Optional error callback.
 * @returns {() => void}
 */
export function subscribeToMaintenanceNotice(onChange, onError) {
  if (typeof onChange !== 'function') {
    return () => {};
  }

  if (!isFirebaseConfigured || !db) {
    onChange(getEmptyMaintenanceNotice());
    return () => {};
  }

  const maintenanceNoticeRef = doc(db, SITE_SETTINGS_COLLECTION, MAINTENANCE_NOTICE_DOC);
  return onSnapshot(
    maintenanceNoticeRef,
    (snapshot) => {
      if (!snapshot.exists()) {
        onChange(getEmptyMaintenanceNotice());
        return;
      }
      onChange(normalizeMaintenanceNotice(snapshot.data()));
    },
    (snapshotError) => {
      if (typeof onError === 'function') {
        onError(snapshotError);
      }
    }
  );
}

/**
 * Creates or updates the maintenance warning notice document.
 * @param {{message: string, isPublished: boolean}} payload Notice payload.
 * @param {string} userId User id that updates the notice.
 * @returns {Promise<void>}
 */
export async function updateMaintenanceNotice(payload, userId) {
  if (!isFirebaseConfigured || !db) {
    throw new Error('Firebase is not configured.');
  }

  const normalizedNotice = normalizeMaintenanceNotice(payload);
  const maintenanceNoticeRef = doc(db, SITE_SETTINGS_COLLECTION, MAINTENANCE_NOTICE_DOC);
  await setDoc(
    maintenanceNoticeRef,
    {
      message: normalizedNotice.message,
      isPublished: normalizedNotice.isPublished,
      updatedBy: String(userId || ''),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}
